import { CircleAlert, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { Button, Card } from "~/components/ui";
import { type AuthContextInterface, useAuth } from "~/features/authn/context";
import { resolveAuthReturnDestination } from "~/features/authn/navigationIntent";
import { rootRoute as R } from "~/routes";

export function SignInPage() {
  const { t } = useTranslation();
  const { user, signIn, signInErrorMessage } = useAuth() as AuthContextInterface;
  const [{ redirectTo }] = useTypedSearchParams(R.signIn);

  const [loading, setLoading] = useState(false);
  const doSignIn = () => {
    setLoading(true);
    void signIn().finally(() => setLoading(false));
  };

  if (user) {
    const destination = resolveAuthReturnDestination({
      intent: { redirectTo },
      origin: window.location.origin,
      signInPath: R.signIn.$buildPath({}),
      defaultDestination: R.$buildPath({}),
    });
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center pt-10">
      {signInErrorMessage ? (
        <div
          role="alert"
          className="flex w-full max-w-[420px] items-start gap-2.5 rounded-[var(--radius-md)] bg-danger-bg p-4 text-danger"
        >
          <CircleAlert className="mt-px size-4 shrink-0" aria-hidden />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-14-bold">{t("feature.common.signInFailed")}</span>
            {/* Sign-in failures surface a raw thrown Error message, which may carry embedded newlines. */}
            <span className="whitespace-pre-line text-14-regular">{signInErrorMessage}</span>
          </div>
        </div>
      ) : null}
      <Card className="mt-2 flex min-w-60 flex-col gap-2 p-4 pt-6">
        <div className="flex justify-center">
          <img src="/puy.png" alt="logo" className="w-[50px]" />
        </div>
        <Button type="button" onClick={doSignIn} disabled={loading} className="w-full capitalize">
          {/* Unlabelled: the button's own text is its accessible name, and sr-only text here
              would be appended to it. */}
          {loading ? <Loader2 className="animate-spin" /> : null}
          {t("ui.signInWithCotenId")}
        </Button>
      </Card>
    </div>
  );
}
